<template>
  <div class="license-form">
    <ul class="nav nav-tabs nav-fill nav-tabs-sm item-content">
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{'active': activeTab === 'scheduling'}"
          @click="changeTabs('scheduling')"
        >
          {{ $t("AdminManagement.LicenseForm.Tabs.Scheduling") }}
        </a>
      </li>
    </ul>

    <div
      v-show="activeTab === 'scheduling'"
      class="container item-content item-forms"
    >
      <div
        class="form-row justify-content-md-center"
        v-for="type in schedulingServiceTypes"
        :key="type.key"
      >
        <div class="col-md-3">
          <label class="custom-control custom-checkbox">
            <input
              type="checkbox"
              v-model="checkbox.Scheduling[type]"
            >
            <span class="custom-control-label">
              {{ $t(`AdminManagement.LicenseForm.SchedulingPlan.${type}`) }}
            </span>
          </label>
        </div>
        <div class="col-md-3">
          <div
            v-if="type === 'Discount'"
            class="input-group"
          >
            <input
              type="number"
              class="form-control"
              v-model="discountPrice"
              :disabled="!checkbox.Scheduling[type]"
              step="10"
              min="0"
            >
            <div class="input-group-append">
              <span class="input-group-text">
                {{ `${$t("Unit.JPY")} ${$t("Unit.PerMonth")}` }}
              </span>
            </div>
          </div>
          <span v-else>
            {{ licenseMap.Scheduling[type].unitPrice }}
            {{ `${$t("Unit.JPY")} ${$t("Unit.PerMonth")}` }}
          </span>
        </div>
        <div class="col-md-3">
          <div class="input-group">
            <input
              type="number"
              class="form-control"
              v-model="countData.Scheduling[type]"
              :disabled="!checkbox.Scheduling[type]"
              min="0"
            >
            <div class="input-group-append">
              <span class="input-group-text">
                {{ $t("Unit.PersonCount") }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <hr class="license-divide-line">

    <div class="container purchase-result">
      <div class="row justify-content-md-center">
        <div class="col-md-3">
          {{ $t("AdminManagement.LicenseForm.Result.SumOfPurchase") }}
        </div>
        <div class="col-md-2">
          {{ purchasedResultData.sum.toLocaleString() }}
          {{ $t("Unit.JPY") }}
        </div>
        <div class="col-md-3">
          {{ $t("AdminManagement.LicenseForm.Result.SumOfLicense") }}
        </div>
        <div class="col-md-2">
          {{ purchasedResultData.count.toLocaleString() }}
          {{ $t("Unit.LicenseCount") }}
        </div>
      </div>
    </div>
    <button
      v-if="pageType === 'setting'"
      type="button"
      class="btn btn-primary btn-block"
      :disabled="isDisabled || purchasedResultData.count < 1"
      @click="openStripe"
    >
      {{ $t("AdminManagement.LicenseForm.Button.NewLicensePurchase") }}
    </button>
    <div
      v-else
      class="row"
    >
      <div class="col-md-4">
        <div class="input-group">
          <div class="input-group-prepend">
            <span class="input-group-text">
              {{ $t("AdminManagement.LicenseForm.PaymentDeadline") }}
            </span>
          </div>
          <input
            type="date"
            class="form-control"
            v-model="dateLimit"
            :min="today"
            pattern="\d{4}-\d{2}-\d{2}"
          >
        </div>
      </div>
      <div class="col-md-8">
        <button
          type="button"
          class="btn btn-primary btn-block"
          :disabled="isDisabled || purchasedResultData.count < 1"
          @click="saveAdminPurchasedLicenseAll"
        >
          {{ $t("AdminManagement.LicenseForm.Button.NewLicenseIssuance") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { LICENCE_MAP } from '@/config/constants'
import { mapState, mapActions } from 'vuex'
import StripeHelper from '@/helper/stripeHelper'

export default {
  props: {
    pageType: {
      type: String,
      default: 'setting'
    },
    isControlable: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    stripe: null,
    activeTab: 'scheduling',
    checkbox: {
      Scheduling: {
        Standard: false,
        Trial: false,
        Free: false,
        Discount: false
      }
    },
    countData: {
      Scheduling: {
        Standard: 0,
        Trial: 0,
        Free: 0,
        Discount: 0
      }
    },
    discountPrice: 600,
    dateLimit: '',
  }),
  computed: {
    ...mapState([
      'lang'
    ]),
    ...mapState('auth', [
      'uid',
    ]),
    licenseMap() {
      if (this.isControlable) {
        let result = Object.assign({}, LICENCE_MAP)
        Object.keys(result).forEach((key, _) => {
          result[key] = Object.assign(result[key], {
            Discount: {
              text: 'Discount',
              unitPrice: Number(this.discountPrice)
            }
          })
        })
        return result
      }
      return LICENCE_MAP
    },
    schedulingServiceTypes() {
      if (!this.licenseMap.Scheduling) return []
      return Object.keys(this.licenseMap.Scheduling)
    },
    purchasedResultData() {
      return Object.keys(this.licenseMap).reduce((acc, cur) => {
        const current = Object.keys(this.licenseMap[cur]).filter((arr) => {
          return this.checkbox[cur][arr]
        }).reduce((a, c) => {
          const count = Number(this.countData[cur][c])
          const sum = Number(count * this.licenseMap[cur][c].unitPrice)
          return {
            sum: Number(a.sum) + sum,
            count: Number(a.count) + count
          }
        }, { sum: 0, count: 0 })
        return {
          sum: Number(acc.sum) + current.sum,
          count: Number(acc.count) + current.count
        }
      }, { sum: 0, count: 0 })
    },
    today() {
      const today = new Date()
      const month = String(today.getMonth()).padStart(2, '0')
      const date = String(today.getDate()).padStart(2, '0')
      return `${today.getFullYear()}-${month}-${date}`
    },
    formattedDateLimit() {
      if (!this.dateLimit) return ''
      return this.$d(new Date(this.dateLimit), 'yymmdd')
    }
  },
  mounted() {
    if (this.pageType === 'adminSetting') {
      this.dateLimit = this.today
    } else {
      this.stripe = new StripeHelper(this.lang, StripeCheckout)
    }
  },
  methods: {
    ...mapActions([
      'setLoadingState',
      'unsetLoadingState'
    ]),
    ...mapActions('auth', [
      'savePurchasedLicense'
    ]),
    changeTabs(target) {
      this.activeTab = target
    },
    openStripe(e) {
      let description = `${this.$t('AdminManagement.LicenseForm.Result.SumOfLicense')}: `
      description += this.purchasedResultData.count
      description += this.$t('Unit.LicenseCount')
      this.stripe.handler.open({
        name: this.$t('AdminManagement.LicenseForm.Title'),
        description: description,
        amount: this.purchasedResultData.sum,
        supportsTokenCallback: true
      })
      e.preventDefault()
    },
    savePurchasedLicenseAll(token) {
      // NOTE: SmartScheduling License
      this.setLoadingState('savePurchasedLicense')
      Promise.all(Object.keys(this.countData.Scheduling).map((plan) => {
        if (this.countData.Scheduling[plan] === 0) return null
        const result = {
          uid: this.uid,
          cardToken: token.id,
          licenseType: plan,
          count: this.countData.Scheduling[plan]
        }
        return this.savePurchasedLicense(result)
      })).then(() => {
        // NOTE: 初期化
        this.countData.Scheduling = {
          Standard: 0,
          Trial: 0,
          Free: 0
        }

        // NOTE: 別のサービスが増えたらここを追加していく
        return Promise.resolve()
      }).finally(() => {
        this.unsetLoadingState('savePurchasedLicense')
      })
    },
    saveAdminPurchasedLicenseAll() {
      const data = {
        countData: this.countData,
        discountPrice: this.discountPrice,
        formattedDateLimit: this.formattedDateLimit
      }
      this.$emit('saveAdminPurchasedLicenseAll', data)
    }
  },
  watch: {
    stripe: {
      handler(val, old) {
        if (!!val.token) {
          // console.log(val.token)
          this.savePurchasedLicenseAll(val.token)
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.license-divide-line {
  border-top: 2px solid #AFBEC7;
}

.license-form {
  .item-content {
    max-height: 40vh;
    overflow: scroll;
  }

  .item-content + .item-content {
    margin-top: 12px;
  }
}

.input-group-text {
  padding: 0 0.5625rem;
}

.item-forms {
  font-size: 1rem;
}

.purchase-result {
  font-size: 1rem;
}
</style>
